import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Group,
  ScrollArea,
  Select,
  Space,
  Stack,
  Text,
  TextInput,
  CopyButton,
  ActionIcon,
  Tooltip,
  Alert,
  Paper,
  Image,
} from "@mantine/core";
import {
  IconX,
  IconShare,
  IconCheck,
  IconCopy,
  IconAlertCircle,
} from "@tabler/icons-react";
import { MeterReturnType } from "../../types";
import { createSharedUrlAsync } from "../../actions/sharedUrl.actions";
import { successToastHook } from "../../components/toasts/success";
import { errorToastHook } from "../../components/toasts/error";

type ShareMetersViewProps = {
  meters: MeterReturnType[];
  close: () => void;
};

const EXPIRY_OPTIONS = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "365 days" },

];

export default function ShareMetersView({ meters, close }: ShareMetersViewProps) {
  const [expiresInDays, setExpiresInDays] = useState<string>("30");
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreateShare = async () => {
    if (!meters.length) return;

    setLoading(true);
    setError(null);

    try {
      const response = await createSharedUrlAsync({
        meter_ids: meters.map((m) => m.id),
        expiresInDays: parseInt(expiresInDays),
      });

      if (response?.data?.data?.token) {
        const baseUrl = window.location.origin;
        const fullUrl = `${baseUrl}/shared/${response.data.data.token}`;
        setShareUrl(fullUrl);
        successToastHook({ msg: "Share link created successfully!" });
      } else if (response?.response?.data?.message) {
        setError(response.response.data.message);
        errorToastHook({ msg: response.response.data.message });
      } else {
        setError("Failed to create share link");
        errorToastHook({ msg: "Failed to create share link" });
      }
    } catch (err) {
      setError("An error occurred");
      errorToastHook({ msg: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShareUrl(null);
    setError(null);
    setExpiresInDays("30");
    close();
  };

  return (
    <Box>
      <Group justify="space-between">
        <Group gap="xs">
          <IconShare size={24} />
          <Text size="lg" fw={600}>
            Share Meters
          </Text>
        </Group>
        <Tooltip label="Close">
          <ActionIcon color="pink" size="sm" onClick={handleClose}>
            <IconX />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Divider my="md" size="md" color="darkblue" />

      <ScrollArea h="80vh">
        <Stack>
          <Paper radius="md" p="sm" withBorder>
            <Text size="sm" fw={600} mb="xs">
              Selected Meters ({meters.length})
            </Text>
            <Stack gap="xs">
              {meters.map((meter) => (
                <Group key={meter.id} gap="xs">
                  <Image
                    src="../assets/images/tap-icon.png"
                    alt="tap"
                    width={20}
                    height={20}
                  />
                  <Text size="sm">{meter.meterSerialNo}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>

          {!shareUrl ? (
            <>
              <Select
                label="Link expires in"
                data={EXPIRY_OPTIONS}
                value={expiresInDays}
                onChange={(value) => setExpiresInDays(value || "30")}
                allowDeselect={false}
              />

              {error && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertCircle size={16} />}
                >
                  {error}
                </Alert>
              )}

              <Space h="xs" />

              <Button
                onClick={handleCreateShare}
                loading={loading}
                leftSection={!loading && <IconShare size={16} />}
                fullWidth
              >
                Create Share Link
              </Button>
            </>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                Share this link with your tenant. They can view the meter data
                without logging in.
              </Text>

              <TextInput
                value={shareUrl}
                readOnly
                rightSection={
                  <CopyButton value={shareUrl} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied!" : "Copy"}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={copied ? "teal" : "gray"}
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck size={16} />
                          ) : (
                            <IconCopy size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                }
              />

              <Alert color="green" variant="light">
                <Text size="sm">
                  Link created! It will expire in {expiresInDays} days.
                </Text>
              </Alert>

              <Space h="xs" />

              <Group justify="flex-end">
                <Button variant="outline" onClick={handleClose}>
                  Done
                </Button>
                <CopyButton value={shareUrl} timeout={2000}>
                  {({ copied, copy }) => (
                    <Button
                      color={copied ? "teal" : "blue"}
                      onClick={copy}
                      leftSection={
                        copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                      }
                    >
                      {copied ? "Copied!" : "Copy Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </>
          )}
        </Stack>
      </ScrollArea>
    </Box>
  );
}
